import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Trophy, DownloadSimple, Copy, ShareNetwork, Mountains } from "@phosphor-icons/react";

const HEADLINE = {
  30: "30 days of cash runway",
  60: "60 days of cash runway",
  90: "90+ days of cash runway",
};
const SUB = {
  30: "A month of projected cushion — built from real bookkeeping, one logged transaction at a time.",
  60: "Two months of projected runway. Breathing room, earned the boring way.",
  90: "A full quarter of projected runway. Steady work, showing up in the numbers.",
};

export function MilestoneShareCard({ open, onClose, businessName, milestone }) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const label = milestone === 90 ? "90+" : milestone;
  const caption = `${businessName} just hit ${HEADLINE[milestone] || `${label} days of cash runway`} with IronRoot 🎉 Building a business, not just surviving the month. #smallbusiness #cashflow #bookkeeping`;

  const render = async () => {
    if (!cardRef.current) return null;
    return html2canvas(cardRef.current, { backgroundColor: null, scale: 2, useCORS: true });
  };

  const download = async () => {
    setBusy(true);
    try {
      const canvas = await render();
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = `ironroot-runway-${label}days.png`;
      a.click();
      toast.success("Card downloaded — share it anywhere!");
    } catch {
      toast.error("Couldn't generate the image. Try a screenshot instead.");
    } finally { setBusy(false); }
  };

  const share = async () => {
    setBusy(true);
    try {
      const canvas = await render();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "ironroot-runway.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text: caption, title: "IronRoot milestone" });
      } else {
        await download();
      }
    } catch {
      /* user cancelled or unsupported */
    } finally { setBusy(false); }
  };

  const copyCaption = async () => {
    try { await navigator.clipboard.writeText(caption); toast.success("Caption copied"); }
    catch { toast.error("Couldn't copy caption."); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-transparent border-0 shadow-none" data-testid="milestone-share-card">
        {/* The shareable card */}
        <div ref={cardRef} style={{
          width: "100%", aspectRatio: "1 / 1", position: "relative",
          background: "linear-gradient(150deg, #0B0F19 0%, #14203B 60%, #0B0F19 100%)",
          borderRadius: 20, padding: 36, display: "flex", flexDirection: "column",
          justifyContent: "space-between", color: "#fff", overflow: "hidden",
          fontFamily: "'Poppins','Inter',sans-serif",
        }}>
          <div style={{ position: "absolute", top: -60, right: -50, width: 220, height: 220, borderRadius: "50%", background: "#22C55E", opacity: 0.16 }} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
            <Mountains size={26} weight="fill" color="#22C55E" />
            <span style={{ fontWeight: 800, fontSize: 20, letterSpacing: "-0.3px" }}>IronRoot</span>
          </div>

          <div style={{ position: "relative" }}>
            <div style={{ width: 72, height: 72, borderRadius: 18, background: "#22C55E", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
              <Trophy size={40} weight="fill" color="#0B0F19" />
            </div>
            <p style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "2px", color: "rgba(255,255,255,0.55)", margin: 0 }}>Milestone unlocked</p>
            <h2 style={{ fontSize: 34, fontWeight: 800, lineHeight: 1.1, margin: "6px 0 10px" }}>{HEADLINE[milestone] || `${label} days of cash runway`}</h2>
            <p style={{ fontSize: 16, color: "rgba(255,255,255,0.8)", margin: 0 }}>{SUB[milestone] || "Cash flow on track."}</p>
          </div>

          <div style={{ position: "relative", borderTop: "1px solid rgba(255,255,255,0.14)", paddingTop: 16 }}>
            <p style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{businessName}</p>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", margin: "2px 0 0" }}>Tracked with IronRoot · {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 p-4 bg-white rounded-b-2xl">
          <button onClick={share} disabled={busy} data-testid="share-card-share"
            className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: "var(--bk-accent)" }}>
            <ShareNetwork size={16} weight="bold" /> Share
          </button>
          <button onClick={download} disabled={busy} data-testid="share-card-download"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold"
            style={{ border: "1px solid var(--bk-border)", color: "var(--bk-text)" }}>
            <DownloadSimple size={16} weight="bold" /> Download
          </button>
          <button onClick={copyCaption} disabled={busy} data-testid="share-card-caption"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold"
            style={{ border: "1px solid var(--bk-border)", color: "var(--bk-text)" }}>
            <Copy size={16} weight="bold" /> Caption
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
